/**
 * Price Configuration Service
 * 
 * Fetches Stripe prices from backend and caches them globally.
 * All price display should use this module instead of hardcoded values.
 * 
 * Usage:
 *   import { useStripePrices } from '../services/priceConfig';
 *   const prices = useStripePrices();
 *   // prices.yearlyPerMonth  → "€4.99"
 *   // prices.monthlyPerMonth → "€5.99"
 *   // prices.yearlyTotal     → "€59.88"
 *   // prices.monthlyTotal    → "€71.88"
 *   // prices.weeklyPrice     → "€1.24"
 *   // prices.loaded          → true/false
 */

import { useState, useEffect } from 'react';
import backendClient from './backendClient';

// ── Module-level cache ──────────────────────────────────────
let _cache = null;
let _fetchPromise = null;

// Currency symbol map
const CURRENCY_SYMBOLS = {
  eur: '€',
  usd: '$',
  gbp: '£',
};

/**
 * Format cents to a display string: "€4.99"
 */
function formatPrice(cents, currency = 'eur') {
  const symbol = CURRENCY_SYMBOLS[currency] || currency.toUpperCase() + ' ';
  const amount = (cents / 100).toFixed(2);
  // Remove trailing .00
  const clean = amount.endsWith('.00') ? amount.slice(0, -3) : amount;
  return `${symbol}${clean}`;
}

/**
 * Hardcoded fallback prices (only used if backend is unreachable)
 */
const FALLBACK = Object.freeze({
  yearlyPerMonth: '€4.99',
  monthlyPerMonth: '€5.99',
  yearlyTotal: '€59.88',
  monthlyTotal: '€71.88',
  weeklyPrice: '€1.15',
  trialDays: 5,
  loaded: true,
});

/**
 * Fetch prices from backend (deduped, cached)
 * Returns formatted price strings ready for display.
 */
export async function fetchStripePrices() {
  if (_cache) return _cache;

  // Deduplicate concurrent calls
  if (!_fetchPromise) {
    _fetchPromise = backendClient.get('/api/stripe/prices', { timeout: 8000 })
      .then(data => {
        const currency = data.currency || 'eur';
        const weeklyFromYearly = Math.round(data.yearly.total / 52);

        _cache = Object.freeze({
          yearlyPerMonth: formatPrice(data.yearly.perMonth, currency),
          monthlyPerMonth: formatPrice(data.monthly.perMonth, currency),
          yearlyTotal: formatPrice(data.yearly.total, currency),
          monthlyTotal: formatPrice(data.monthly.total, currency),
          weeklyPrice: formatPrice(weeklyFromYearly, currency),
          trialDays: Number.isFinite(Number(data.trialDays)) ? Math.max(0, Math.floor(Number(data.trialDays))) : 5,
          loaded: true,
        });
        return _cache;
      })
      .catch(err => {
        console.warn('[PriceConfig] Failed to fetch prices, using fallback:', err.message);
        _cache = FALLBACK;
        return _cache;
      })
      .finally(() => {
        _fetchPromise = null;
      });
  }

  return _fetchPromise;
}

/**
 * React hook — returns price strings (or fallback while loading).
 * Triggers one fetch on first mount across entire app.
 */
export function useStripePrices() {
  const [prices, setPrices] = useState(_cache || FALLBACK);

  useEffect(() => {
    if (_cache) {
      setPrices(_cache);
      return;
    }
    let cancelled = false;
    fetchStripePrices().then(p => {
      if (!cancelled) setPrices(p);
    });
    return () => { cancelled = true; };
  }, []);

  return prices;
}
